import React, {Component, createRef} from "react";
import {Box, Checkbox, IconButton, List, MenuItem, Select, Stack, Tooltip, Typography} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CircleIcon from "@mui/icons-material/Circle";
import AnnotationsDownload from "./annotationsDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import MergeIcon from '@mui/icons-material/Merge';
import {connect} from "react-redux";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AnnotationAccordion from "./AnnotationAccordion";
import {
	addAnnotation,
	deleteAnnotation,
	deleteMultipleAnnotations,
	updateSelectedAnnotationsList
} from "../../../redux_actions/maps.state.action";
import {Feature} from "ol";
import LineString from "ol/geom/LineString";
import {store} from "../../../helper/store";
import {AnnotationsConstants} from "../../../utils/const";
import {getFormattedLength, getLoggedInUserId, getUperpixel, isSuperuser, isTrue} from "../../../utils/utils";
import {getCenter} from "ol/extent";
import {notifyInfo, notifyWarning} from "../utils/display.notification";
import { aclPermissions, hasPermissions } from "const/ACLPermissions";
import cookie from "react-cookies";

class AnnotationsExpandableList extends Component {

	constructor(props) {
		super(props);

		this.state = {
			colorFilter: "ALL",
			isShiftDown: false,
            lastSelectedItem: -1,
		}

		this.handleKeyUp = this.handleKeyUp.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);

		this.is_superuser = isSuperuser();

		this.accordionRefs = {}
		this.updateRefs();
	}

	componentDidMount = () => {
        document.addEventListener("keyup", this.handleKeyUp, false);
        document.addEventListener("keydown", this.handleKeyDown, false);
    }

    componentWillUnmount() {
        document.removeEventListener("keyup", this.handleKeyUp);
        document.removeEventListener("keydown", this.handleKeyDown);
    }

	componentDidUpdate(prevProps) {
		if (prevProps.annotations.length !== this.props.annotations.length) {
			this.updateRefs();
		}
	}

	updateRefs = () => {
		this.props.annotations.forEach(annotation => {
			if (!this.accordionRefs[annotation.id]) {
				this.accordionRefs[annotation.id] = createRef();
			}
		});
	};

    handleKeyUp(e) {
        if (e.key === "Shift" && this.state.isShiftDown) {
            this.setState({ isShiftDown: false });
        }
    }
      
    handleKeyDown(e) {
        if (e.key === "Shift" && !this.state.isShiftDown) {
            this.setState({ isShiftDown: true });
        }

		// On OpenSeadragon, AnnotationOverlayOSD handles Delete so it also works with this app closed.
		if(e.key === "Delete" && !this.props.slidemap?._isOpenSeadragon) {
			this.deleteAnnotations();
		}

    }

	hasDeletePermission = hasPermissions([aclPermissions.delete_other_user_annotation])
	hasDeleteOwnPermission = hasPermissions([aclPermissions.delete_own_annotation])
	hasEditPermission = hasPermissions([aclPermissions.update_annotation])
	hasViewPermission = hasPermissions([aclPermissions.view_own_annotation])

	deleteAnnotations = () => {
		let selectAnnotations;
		if (this.hasDeletePermission) {
			selectAnnotations = this.props.annotations.filter(annotation => annotation.checked)
		} else if (this.hasDeleteOwnPermission) {
			selectAnnotations = this.props.annotations.filter(annotation => annotation.checked && annotation.creator === getLoggedInUserId())
			notifyWarning('anno', "You don't have permission to delete other user annotations! Only your annotations will be deleted.")
		} else {
			notifyWarning('anno', "You don't have permission to delete annotations!")
			return;
		}

		this.props.dispatch(deleteMultipleAnnotations(this.props.activeMapId, this.props.slideId, selectAnnotations));
		store.dispatch(updateSelectedAnnotationsList(this.props.activeMapId, selectAnnotations, false))
	}

	handleAnnotationSelect = (e, annotations, selectedIdx) => {
        let lastSelectedItemIdx = this.state.lastSelectedItem;
        let isMultiSelectCase = (this.state.isShiftDown && lastSelectedItemIdx > -1);
        let rangeStartIdx = selectedIdx;
        let rangeEndIdx = selectedIdx;

        if(isMultiSelectCase) {
            rangeStartIdx = Math.min(selectedIdx, lastSelectedItemIdx);
            rangeEndIdx = Math.max(selectedIdx, lastSelectedItemIdx);
        } 
        
        let performDeselect = annotations[ selectedIdx ].checked;
        
		let annotationsUpdated = [];
        for(let i=rangeStartIdx; i<=rangeEndIdx; i++){
            annotations[ i ].checked = !performDeselect;
			annotationsUpdated.push(annotations[ i ].id);
        }
        
		store.dispatch(updateSelectedAnnotationsList(this.props.activeMapId, annotationsUpdated, !performDeselect));

        this.setState({
            lastSelectedItem: selectedIdx
        })
	}

	checkTrigger = () => {
		if (this.props.trigger.expandAnnotation) {
			this.props.annotations.forEach((annotation, idx) => {
				const shouldExpand = this.props.trigger.expandAnnotation === annotation.id;
				annotation.expanded = shouldExpand;
				if (shouldExpand && this.accordionRefs[annotation.id]?.current) {
					this.accordionRefs[annotation.id].current.scrollIntoView({ behavior: 'smooth' });
				}
			});
			delete this.props.trigger.expandAnnotation;
			this.setState({});
		}
	}

	changeFilterColor = (e) => {
		if (this.props.onColorFilter)
			this.props.onColorFilter(e.target.value);
		this.setState({
			colorFilter: e.target.value,
		});
	}

	actionOnSelected = () => {
		this.props.actionOnSelected && this.props.actionOnSelected(
			this.props.annotations.filter(annotation => annotation.checked).map(annotation => {
				if (this.props.deselectPostAction) {
					annotation.checked = false;
				}
				return annotation;
			}));
		this.setState({});
	}

	getDefaultTopComponent = () => {
		let menuItems = [];
		menuItems.push(
			<MenuItem value={"ALL"}>
				<Typography sx={{color:"white"}}>ALL</Typography>
			</MenuItem>
		)
		Object.entries(this.props.colors).forEach(([name, color]) =>
			menuItems.push(
				<MenuItem label={name} value={color.toLowerCase()}>
					<CircleIcon fontSize="small" sx={{color:color.toLowerCase()}}/>
					{this.props.showColorLabels ? name : null}
				</MenuItem>
			)
		)
		return <Stack direction={"row"} paddingX={1.2} paddingY={0.5} paddingRight={0} justifyContent={"space-between"}>
			<Select variant={"filled"} value={this.state.colorFilter} onChange={this.changeFilterColor} disabled={!this.hasViewPermission}>
				{menuItems}
			</Select>
			{this.props.showDownload ? <AnnotationsDownload slideId={this.props.slideId} annoDrawer={0}/> : undefined}
		</Stack>
	}

	getGeometricParams = (geometry) => ({
		area: 0,
		perimeter: geometry.getLength() * getUperpixel(),
		coordinates: geometry.getCoordinates(),
	});
	getArea = (geometry) => geometry.getArea();
	getCoordinates = (geometry) => geometry.getCoordinates();
	getTitle = (feature) => `Merged[${this.mergedCount}] R ${getFormattedLength(this.getGeometricParams(feature.getGeometry()).perimeter)}`;
	addMergedAnnotation = () => {
		this.mergedAnnotation = [[]];
		this.mergedCount = 0;
		this.annoFlag = false;
		this.props.annotations.filter(annotation => annotation.checked)
			.forEach(annotation => {
				if (annotation['shape'] !== AnnotationsConstants.LINE) {
					notifyInfo('anno', "We can merge Line Annotations only!");
					this.annoFlag = true;
				} else {
					this.mergedAnnotation = this.mergedAnnotation.concat((JSON.parse(annotation['new_bounds'])).reverse());
					this.mergedCount += 1;
				}
			});

		if (this.mergedCount === 1) {
			notifyInfo('anno', "Select more than one Line Annotations inorder to merge!");
			this.annoFlag = true;
		}
		const feature = new Feature({
			geometry: new LineString(this.mergedAnnotation.slice(1)),
		});
		let geometry = feature.getGeometry();
		if (!this.annoFlag) {
			store.dispatch(addAnnotation(this.props.activeMapId, {
				area: 0,
				perimeter: geometry.getLength(),
				color: this.props.selectedDrawColor,
				new_bounds: JSON.stringify(this.getCoordinates(geometry).reverse()),
				slide: this.props.slide.id,
				creator: getLoggedInUserId(),
				shape: AnnotationsConstants.LINE,
				tool_type: "Freeline",
				center: getCenter(geometry.getExtent()).toString(),
				// OpenSeadragon has no OL layer; the merged line keeps the drawer of the merged annotations.
				anno_drawer: this.props.layer ? this.props.layer.values_.drawer
					: this.props.annotations.find(annotation => annotation.checked)?.anno_drawer,
				created_z_level: this.props.slidemap.getView().getZoom(),
				title: this.getTitle(feature),
			}));
			this.props.annotations.filter(annotation => annotation.checked)
				.forEach(annotation =>
					this.props.dispatch(deleteAnnotation(this.props.activeMapId, annotation)));
		}
	}

	getMultipleSelectTopComponent = () =>
		<Stack direction={"row"} paddingX={1.2} justifyContent={"space-between"}>
			<Tooltip title={"Select All"} placement={"right"}>
				<Checkbox edge={"start"}
						  checked={this.props.annotations.reduce((checked, annotation) =>
							  checked && annotation.checked, true) || false}    // || false to handle undefined
						  disableRipple size={'medium'}
						  icon={<CheckBoxOutlineBlankIcon/>}
						  checkedIcon={<CheckBoxIcon color={"secondary"}/>}
				          onClick={e => {
								let annotationsUpdated = [];
					        	this.props.annotations.forEach(annotation => {
									annotation.checked = e.target.checked
									annotationsUpdated.push(annotation.id);
								});
								store.dispatch(updateSelectedAnnotationsList(this.props.activeMapId, annotationsUpdated, e.target.checked))

							  this.setState({});
						  }}/>
			</Tooltip>
			{this.props.actionOnSelected ?
				<Tooltip title={this.props.actionTooltip || "Run Action on selected annotation"} placement={"top"}>
					<IconButton onClick={this.actionOnSelected}
								color={"secondary"}><PlayArrowIcon/></IconButton>
				</Tooltip> :
				undefined
			}
			{this.props.showMergeAnnotations && <Tooltip title={"Merge Selected Annotations"} placement={"right"}>
				<IconButton onClick={() => this.addMergedAnnotation()} color={'secondary'} disabled={!this.hasEditPermission}>
					<MergeIcon/>
				</IconButton>
			</Tooltip>}
			<Tooltip title={"Delete Selected Annotations"} placement={"right"}>
				<IconButton onClick={this.deleteAnnotations}
							color={'secondary'}
				>
					<DeleteIcon/>
				</IconButton>
			</Tooltip>
		</Stack>
	getTopComponent = () => {
		let warningMessage = ""
		if (this.is_superuser) {
			warningMessage = "Annotations created by admin will not be visible to other users"
		}
		return (<><Typography sx={{color: "red"}}>{warningMessage}</Typography><Box height={30}>
			{this.props.annotations.reduce((previousValue, currentValue) =>
				previousValue || currentValue.checked, false) ? this.getMultipleSelectTopComponent() :
				this.getDefaultTopComponent()}
		</Box></>)
	}

	render() {
		this.checkTrigger();

		// if onColorFilter function is passed then do not filter internally, passed function will filter externally
		let annotations = this.props.onColorFilter ? this.props.annotations :
			this.props.annotations.filter(annotation => this.state.colorFilter === 'ALL' ||
				annotation.color.toLowerCase() === this.state.colorFilter);

		return <Box>
			{this.getTopComponent()}
			<List>
				{this.hasViewPermission ?
					annotations.map((annotation, idx) =>
						<AnnotationAccordion annotation={annotation}
											 key={annotation.id}
											 accordionRef={this.accordionRefs[annotation.id]}
											 onAccordionChange={() => {
							this.props.annotations.forEach(annotation_ =>
								annotation_.expanded = annotation.id === annotation_.id && !annotation.expanded
							);
							this.setState({});
						}}
							onCheckboxChange={(e) => { this.handleAnnotationSelect(e, annotations, idx)}}
							onAnnotationClick={this.props.onAnnotationClick} />
					)
					: <></>
				}
			</List>
		</Box>
	}

}const mapStateToProps = (state) => {
	let {activeMapId, activeAnnoDrawer, selectedDrawColor} = state.gammaStateReducer;
	let {slide, slidemap} = state.mapsStateReducer[activeMapId];
	let layer = slidemap && !slidemap._isOpenSeadragon ?
		slidemap.getLayers().getArray().find(layer => layer.get('drawer') === activeAnnoDrawer) : null;
	let trigger = state.triggerReducer;
	return {activeMapId, trigger, selectedDrawColor, slide, slidemap, layer};
}

export default connect(mapStateToProps)(AnnotationsExpandableList)